import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight,
  Ban,
  Eye,
  Lock,
  UserCog,
  UserPlus,
  XCircle,
} from 'lucide-react';

import { Badge, Button, TableController, type TableHeader } from '@/shared/ui';
import {
  USER_STATUS_BADGE_VARIANT,
  USER_STATUS_I18N_KEY,
} from '@entities/User';
import type { User } from '@psp-aggregator/sdk';

import { formatRelativeActivity } from '../../libs/format-relative-activity';

export type UsersTableMode = 'pending' | 'internal' | 'external';

export interface UsersTableProps {
  busyUserId?: string;
  filtered?: boolean;
  guardLastAdmin?: (user: User) => boolean;
  guardSelf?: (user: User) => boolean;
  mode: UsersTableMode;
  onAssign?: (user: User) => void;
  onBlock?: (user: User) => void;
  onChangeRole?: (user: User) => void;
  onClearFilters?: () => void;
  onGoToDeals?: (user: User) => void;
  onReject?: (user: User) => void;
  onUnblock?: (user: User) => void;
  onView: (user: User) => void;
  users: User[];
}

export const UsersTable: FC<UsersTableProps> = ({
  busyUserId,
  filtered = false,
  guardLastAdmin,
  guardSelf,
  mode,
  onAssign,
  onBlock,
  onChangeRole,
  onClearFilters,
  onGoToDeals,
  onReject,
  onUnblock,
  onView,
  users,
}) => {
  const { t } = useTranslation();
  const isPending = mode === 'pending';

  const headers: TableHeader[] = [
    { key: true, title: t('pages.admin.users.columns.email'), value: 'email' },
    { title: t('pages.admin.users.columns.telegram'), value: 'telegram' },
    { title: t('pages.admin.users.columns.status'), value: 'status' },
    {
      title: t('pages.admin.users.columns.lastActivity'),
      value: 'lastActivity',
    },
    { title: t('pages.admin.users.columns.actions'), value: 'actions' },
  ];

  const rows = users.map((user) => {
    const isSelf = guardSelf?.(user) ?? false;
    const lastAdmin = guardLastAdmin?.(user) ?? false;
    const busy = busyUserId === user.id;

    return {
      actions: (
        <div className="inline-flex items-center gap-1">
          {isPending ? (
            <>
              <Button
                aria-label={t('buttons.assignRole')}
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={() => onAssign?.(user)}>
                <UserPlus className="h-3.5 w-3.5" />
              </Button>
              <Button
                aria-label={t('buttons.reject')}
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={() => onReject?.(user)}>
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                aria-label={t('buttons.changeRole')}
                disabled={isSelf || busy}
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={() => onChangeRole?.(user)}>
                <UserCog className="h-3.5 w-3.5" />
              </Button>
              {user.status === 'blocked' ? (
                <Button
                  aria-label={t('buttons.unblock')}
                  disabled={isSelf || busy}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                  onClick={() => onUnblock?.(user)}>
                  <Lock className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button
                  aria-label={t('buttons.block')}
                  disabled={isSelf || busy || lastAdmin}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                  onClick={() => onBlock?.(user)}>
                  <Ban className="h-3.5 w-3.5" />
                </Button>
              )}
              {mode === 'external' && !!onGoToDeals && (
                <Button
                  aria-label={t('buttons.viewDeals')}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                  onClick={() => onGoToDeals(user)}>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
          <Button
            aria-label={t('buttons.view')}
            size="icon-sm"
            type="button"
            variant="ghost"
            onClick={() => onView(user)}>
            <Eye className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
      email: (
        <span className="block max-w-[260px] truncate text-foreground">
          {user.email}
        </span>
      ),
      id: user.id,
      lastActivity: (
        <span className="whitespace-nowrap text-muted-foreground">
          {formatRelativeActivity(user.updated_at, t)}
        </span>
      ),
      status: (
        <Badge variant={USER_STATUS_BADGE_VARIANT[user.status]}>
          {t(USER_STATUS_I18N_KEY[user.status])}
        </Badge>
      ),
      telegram: user.telegram || '—',
    };
  });

  return (
    <TableController
      data={rows}
      emptyState={{
        filtered,
        onClearFilters,
      }}
      headers={headers}
      mobileItemConfig={{
        actionsSlot: { key: 'actions' },
        subtitleSlot: { key: 'telegram' },
        titleSlot: { key: 'email' },
        valueSlot: { key: 'status' },
      }}
    />
  );
};

UsersTable.displayName = 'UsersTable';
